import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, StrictEffect, takeLatest } from "redux-saga/effects";

import { DataType, FeatureType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";
import { Transaction } from "@/jenesys-types/jenesys-entities/config";

import { performFeatureAction } from "@/services/api";
import { updateItemSaga } from "@/store/crud/crud.saga";
import { updateItem } from "@/store/crud/update";
import { loadItem } from "@/store/crud/view";
import { fetchAdjacentDocumentRequest } from "@/store/features/transactions/entityNavigation/entityNavigation";
import { toastError, toastSuccess } from "@/util/helpers";
import { performanceMonitor } from "@/utils/performance";

import {
  publishTransaction,
  publishTransactionFailure,
  PublishTransactionPayload,
  publishTransactionSuccess,
  updateDocumentStatusFailure,
  UpdateDocumentStatusPayload,
  updateDocumentStatusRequest,
  updateDocumentStatusSuccess
} from "./publish";

const LOCAL_STORAGE_STATUS_KEY = "lastDocumentNavigationStatus";

function* publishTransactionSaga(
  action: PayloadAction<PublishTransactionPayload>
): Generator<StrictEffect, void, any> {
  const { documentId, companyId, transaction } = action.payload;
  const metricName = "Saga.publishTransactionSaga";
  const metricId = performanceMonitor.start(metricName, {
    sagaInstance: `${DataType.Transaction}-${documentId}`,
    ...action.payload
  });

  const status = localStorage.getItem(LOCAL_STORAGE_STATUS_KEY);

  try {
    yield call(
      updateItemSaga,
      updateItem(
        DataType.Transaction,
        documentId,
        {
          ...(transaction.extracted_data || {}),
          id: documentId
        },
        undefined,
        undefined,
        undefined,
        undefined,
        true
      )
    );

    const response: Transaction = yield call(
      performFeatureAction,
      FeatureType.Bookkeeping,
      "synchronizeTransaction",
      { companyId, documentId }
    );

    yield put(publishTransactionSuccess(documentId, response));
    yield put(loadItem(DataType.Transaction, documentId, [], {}));
    toastSuccess("Document sent for publishing successfully");

    yield put(
      fetchAdjacentDocumentRequest(
        companyId,
        documentId,
        "next",
        status || "REVIEWED",
        {
          onFinalDocument: {
            redirectTo: `/client/${companyId}/transactions/management`
          },
          onFailure: {
            redirectTo: `/client/${companyId}/transactions/management`
          }
        }
      )
    );
  } catch (error) {
    yield put(publishTransactionFailure(documentId, extractError(error)));
    toastError(extractError(error).message);
  } finally {
    performanceMonitor.stop(metricId);
  }
}

function* updateDocumentStatusSaga(
  action: PayloadAction<UpdateDocumentStatusPayload>
): Generator<StrictEffect, void, any> {
  const { documentId, companyId, status, published_manually } = action.payload;
  const metricName = "Saga.updateDocumentStatusSaga";
  const metricId = performanceMonitor.start(metricName, {
    sagaInstance: `${DataType.Transaction}-${documentId}`,
    ...action.payload
  });

  try {
    yield call(
      performFeatureAction,
      FeatureType.Bookkeeping,
      "updateDocumentStatus",
      { companyId, documentId },
      {},
      { status, published_manually }
    );

    yield put(updateDocumentStatusSuccess(documentId, status));
    yield put(loadItem(DataType.Transaction, documentId, [], {}));
    toastSuccess("Document status updated successfully");
  } catch (error) {
    yield put(updateDocumentStatusFailure(documentId, extractError(error)));
    toastError(extractError(error).message);
  } finally {
    performanceMonitor.stop(metricId);
  }
}

export default function* publishSaga() {
  yield takeLatest(publishTransaction.type, publishTransactionSaga);
  yield takeLatest(updateDocumentStatusRequest.type, updateDocumentStatusSaga);
}
